import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Link,
  useLocation,
  useParams,
} from "react-router-dom";

import { auth } from "./firebaseConfig";
import "./userInfo.css";

const API_URL = "https://api.malidag.com";

function UserInfo() {
  const { paymentId } = useParams();
  const location = useLocation();

  const [order, setOrder] = useState(
    location.state?.order || null
  );

  const [loading, setLoading] = useState(
    !location.state?.order
  );

  const [error, setError] = useState("");

  /*
  |--------------------------------------------------------------------------
  | Load transaction
  |--------------------------------------------------------------------------
  |
  | If we arrived from UserPayment, React Router already gave us the order.
  | If the page is refreshed/directly opened, fetch it securely.
  |
  */

  useEffect(() => {
    if (order) {
      return;
    }

    let cancelled = false;

    const loadOrder = async () => {
      try {
        setLoading(true);
        setError("");

        const user = auth.currentUser;

        if (!user) {
          throw new Error(
            "Admin authentication is required."
          );
        }

        const token =
          await user.getIdToken();

       const response = await fetch(
        `${API_URL}/api/admin/stripe/transaction/${encodeURIComponent(
          paymentId
        )}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error(
              "Your admin session has expired."
            );
          }

          if (response.status === 403) {
            throw new Error(
              "This account does not have admin access."
            );
          }

          if (response.status === 404) {
            throw new Error(
              "Transaction not found."
            );
          }

          throw new Error(
            `Failed to load transaction (${response.status}).`
          );
        }

        const data =
          await response.json();

        if (!cancelled) {
          setOrder(data?.order || null);
        }
      } catch (err) {
        console.error(
          "Failed to load Stripe transaction:",
          err
        );

        if (!cancelled) {
          setError(
            err?.message ||
              "Failed to load transaction."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadOrder();

    return () => {
      cancelled = true;
    };
  }, [paymentId, order]);

  /*
  |--------------------------------------------------------------------------
  | Helpers
  |--------------------------------------------------------------------------
  */

  const formatMoney = (
    amount,
    currency = "USD"
  ) => {
    const value = Number(amount);

    if (!Number.isFinite(value)) {
      return "—";
    }

    try {
      return new Intl.NumberFormat(
        "en-US",
        {
          style: "currency",
          currency: String(
            currency
          ).toUpperCase(),
        }
      ).format(value);
    } catch {
      return `${value.toFixed(2)} ${currency}`;
    }
  };

  const formatDate = (value) => {
    if (!value) {
      return "—";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "—";
    }

    return date.toLocaleString(
      "en-US",
      {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  };

  const displayValue = (value) => {
    const clean = String(
      value ?? ""
    ).trim();

    if (
      !clean ||
      clean.toLowerCase() ===
        "nocolor" ||
      clean.toLowerCase() ===
        "nosize"
    ) {
      return "—";
    }

    return clean;
  };

  /*
  |--------------------------------------------------------------------------
  | Derived values
  |--------------------------------------------------------------------------
  */

  const items = useMemo(
    () =>
      Array.isArray(order?.items)
        ? order.items
        : [],
    [order]
  );

  const totalQuantity = useMemo(
    () =>
      items.reduce(
        (total, item) => {
          const quantity = Number(
            item?.quantity
          );

          return (
            total +
            (Number.isFinite(quantity)
              ? quantity
              : 0)
          );
        },
        0
      ),
    [items]
  );


 const hasCompleteBrandCost = useMemo(
  () =>
    items.length > 0 &&
    items.every((item) => {
      const brandPrice =
        Number(item?.brandPrice);

      return Number.isFinite(brandPrice);
    }),
  [items]
);

const totalBrandCost = useMemo(() => {
  if (!hasCompleteBrandCost) {
    return null;
  }

  return items.reduce((total, item) => {
    const brandPrice =
      Number(item.brandPrice);

    const quantity =
      Number(item?.quantity || 1);

    return (
      total +
      brandPrice *
        (Number.isFinite(quantity)
          ? quantity
          : 1)
    );
  }, 0);
}, [items, hasCompleteBrandCost]);

const grossProfit = useMemo(() => {
  const revenue =
    Number(order?.canonicalUsdAmount);

  if (
    !Number.isFinite(revenue) ||
    totalBrandCost === null
  ) {
    return null;
  }

  return revenue - totalBrandCost;
}, [
  order?.canonicalUsdAmount,
  totalBrandCost,
]);

const grossMargin = useMemo(() => {
  const revenue =
    Number(order?.canonicalUsdAmount);

  if (
    !Number.isFinite(revenue) ||
    revenue <= 0 ||
    grossProfit === null
  ) {
    return null;
  }

  return (
    grossProfit /
    revenue
  ) * 100;
}, [
  order?.canonicalUsdAmount,
  grossProfit,
]);
  /*
  |--------------------------------------------------------------------------
  | States
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <div className="transaction-state">
        <div className="transaction-spinner" />

        <h2>
          Loading transaction
        </h2>

        <p>
          Retrieving Stripe payment
          details...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="transaction-state transaction-error">
        <div className="transaction-state-icon">
          !
        </div>

        <h2>
          Unable to load transaction
        </h2>

        <p>{error}</p>

        <Link
          to="/userorder"
          className="transaction-back-button"
        >
          Back to payments
        </Link>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="transaction-state">
        <h2>
          Transaction not found
        </h2>

        <Link
          to="/userorder"
          className="transaction-back-button"
        >
          Back to payments
        </Link>
      </div>
    );
  }

  const delivery =
    order.delivery || {};

  return (
    <div className="transaction-page">
      <div className="transaction-container">

        {/* NAVIGATION */}

        <div className="transaction-navigation">
          <Link
            to="/userorder"
            className="transaction-back-link"
          >
            ← Payments
          </Link>
        </div>

        {/* HEADER */}

        <header className="transaction-header">
          <div>
            <div className="transaction-eyebrow">
              Malidag Admin
            </div>

            <h1>
              Transaction details
            </h1>

            <p>
              Payment and customer
              information for this order.
            </p>
          </div>

          <div className="transaction-paid-badge">
            <span />
            Paid
          </div>
        </header>

        {/* PAYMENT SUMMARY */}

        <section className="transaction-summary-card">

          <div className="transaction-summary-main">
            <span className="transaction-label">
              Payment amount
            </span>

            <strong className="transaction-total">
              {formatMoney(
                order.amount,
                order.currency
              )}
            </strong>

            <span className="transaction-usd-value">
              {formatMoney(
                order.canonicalUsdAmount,
                "USD"
              )}{" "}
              canonical USD value
            </span>
          </div>

         <div className="transaction-summary-stats">

  <div>
    <span>Market</span>
    <strong>
      {String(
        order.countryCode || "—"
      ).toUpperCase()}
    </strong>
  </div>

  <div>
    <span>Items</span>
    <strong>
      {totalQuantity}
    </strong>
  </div>

  <div>
    <span>Product cost</span>
    <strong>
      {totalBrandCost !== null
        ? formatMoney(
            totalBrandCost,
            "USD"
          )
        : "—"}
    </strong>
  </div>

  <div>
    <span>Gross profit</span>
    <strong>
      {grossProfit !== null
        ? formatMoney(
            grossProfit,
            "USD"
          )
        : "—"}
    </strong>
  </div>

  <div>
    <span>Gross margin</span>
    <strong>
      {grossMargin !== null
        ? `${grossMargin.toFixed(2)}%`
        : "—"}
    </strong>
  </div>

</div>
        </section>

        {/* TWO COLUMN INFO */}

        <div className="transaction-grid">

          {/* PAYMENT */}

          <section className="transaction-card">
            <div className="transaction-card-header">
              <div>
                <span className="transaction-section-icon">
                  $
                </span>

                <div>
                  <h2>
                    Payment
                  </h2>

                  <p>
                    Stripe transaction
                    information
                  </p>
                </div>
              </div>
            </div>

            <div className="transaction-detail-list">

              <DetailRow
                label="Status"
                value={
                  <span className="transaction-inline-paid">
                    <span />
                    Paid
                  </span>
                }
              />

              <DetailRow
                label="Provider"
                value="Stripe"
              />

              <DetailRow
                label="Method"
                value={
                  order.paymentMethod ===
                  "card"
                    ? "Card"
                    : displayValue(
                        order.paymentMethod
                      )
                }
              />

              <DetailRow
                label="Payment Intent"
                value={
                  order.stripePaymentIntentId
                }
                mono
                copy
              />

              <DetailRow
                label="Payment Method ID"
                value={
                  order.stripePaymentMethod ||
                  "—"
                }
                mono
                copy={
                  Boolean(
                    order.stripePaymentMethod
                  )
                }
              />

              <DetailRow
                label="Stripe Event"
                value={
                  order.stripeEventId ||
                  "—"
                }
                mono
                copy={
                  Boolean(
                    order.stripeEventId
                  )
                }
              />

              <DetailRow
                label="Paid"
                value={formatDate(
                  order.paidAt
                )}
              />

            </div>
          </section>

          {/* CUSTOMER */}

          <section className="transaction-card">
            <div className="transaction-card-header">
              <div>
                <span className="transaction-section-icon">
                  U
                </span>

                <div>
                  <h2>
                    Customer
                  </h2>

                  <p>
                    Delivery information
                  </p>
                </div>
              </div>
            </div>

            <div className="transaction-detail-list">

              <DetailRow
                label="Name"
                value={
                  delivery.fullName ||
                  "—"
                }
              />

              <DetailRow
                label="Email"
                value={
                  delivery.email ||
                  "—"
                }
              />

              <DetailRow
                label="Company"
                value={
                  delivery.companyName ||
                  "—"
                }
              />

              <DetailRow
                label="Address"
                value={
                  delivery.streetAddress ||
                  "—"
                }
              />

              <DetailRow
                label="Town / City"
                value={
                  delivery.town ||
                  "—"
                }
              />

              <DetailRow
                label="Postal code"
                value={
                  delivery.postalCode ||
                  "—"
                }
              />

              <DetailRow
                label="Country"
                value={
                  delivery.country ||
                  "—"
                }
              />

            </div>
          </section>

        </div>

        {/* ITEMS */}

        <section className="transaction-items-card">

          <div className="transaction-items-header">
            <div>
              <h2>
                Order items
              </h2>

              <p>
                {totalQuantity}{" "}
                {totalQuantity === 1
                  ? "item"
                  : "items"}{" "}
                purchased
              </p>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="transaction-no-items">
              No items recorded for this
              transaction.
            </div>
          ) : (
            <div className="transaction-items-list">

              {items.map(
                (item, index) => (
                  <div
                    className="transaction-item"
                    key={
                      item?.itemId ||
                      item?.id ||
                      index
                    }
                  >

                    {/* IMAGE */}

                    <div className="transaction-item-image-wrapper">
                      {item?.image ? (
                        <img
                          src={item.image}
                          alt={
                            item?.name ||
                            "Product"
                          }
                          className="transaction-item-image"
                        />
                      ) : (
                        <div className="transaction-item-image-placeholder">
                          No image
                        </div>
                      )}
                    </div>

                    {/* INFO */}

                    <div className="transaction-item-info">

                      <h3>
                        {item?.name ||
                          "Product"}
                      </h3>

                      <span className="transaction-item-id">
                        {item?.itemId ||
                          item?.id ||
                          "—"}
                      </span>

                     <div className="transaction-item-options">

  <span>
    <small>Brand</small>
    {displayValue(item?.brandName)}
  </span>

  <span>
    <small>Color</small>
    {displayValue(item?.color)}
  </span>

  <span>
    <small>Size</small>
    {displayValue(item?.size)}
  </span>

  <span>
    <small>Quantity</small>
    {item?.quantity || 1}
  </span>

</div>
                    </div>

                    {/* PRICE */}

                    <div className="transaction-item-price">

  <span>
    Selling price
  </span>

  <strong>
    {formatMoney(
      item?.usdUnitPrice,
      "USD"
    )}
  </strong>

  <small>
    Brand cost{" "}
    {formatMoney(
      item?.brandPrice,
      "USD"
    )}
  </small>

  <small>
    Line total{" "}
    {formatMoney(
      item?.usdLineTotal,
      "USD"
    )}
  </small>

  {Number.isFinite(Number(item?.brandPrice)) &&
    Number.isFinite(Number(item?.usdLineTotal)) && (
      <small className="transaction-item-profit">
        Gross profit{" "}
        {formatMoney(
          Number(item.usdLineTotal) -
            Number(item.brandPrice) *
              Number(item.quantity || 1),
          "USD"
        )}
      </small>
    )}

</div>

                  </div>
                )
              )}

            </div>
          )}
        </section>

        {/* TECHNICAL */}

        <section className="transaction-card transaction-technical-card">

          <div className="transaction-card-header">
            <div>
              <span className="transaction-section-icon">
                #
              </span>

              <div>
                <h2>
                  Order record
                </h2>

                <p>
                  Internal Malidag
                  transaction data
                </p>
              </div>
            </div>
          </div>

          <div className="transaction-detail-list">

            <DetailRow
              label="User ID"
              value={
                order.userId || "—"
              }
              mono
              copy={
                Boolean(order.userId)
              }
            />

            <DetailRow
              label="Mongo record"
              value={
                order._id || "—"
              }
              mono
            />

            <DetailRow
              label="Basket checkout"
              value={
                order.basket
                  ? "Yes"
                  : "No"
              }
            />

            <DetailRow
              label="Created"
              value={formatDate(
                order.createdAt
              )}
            />

            <DetailRow
              label="Last updated"
              value={formatDate(
                order.updatedAt
              )}
            />

          </div>
        </section>

      </div>
    </div>
  );
}

/*
|--------------------------------------------------------------------------
| Detail row
|--------------------------------------------------------------------------
*/

function DetailRow({
  label,
  value,
  mono = false,
  copy = false,
}) {
  const [copied, setCopied] =
    useState(false);

  const handleCopy = async () => {
    if (
      !copy ||
      typeof value !== "string"
    ) {
      return;
    }

    try {
      await navigator.clipboard.writeText(
        value
      );

      setCopied(true);

      window.setTimeout(
        () => setCopied(false),
        1200
      );
    } catch (error) {
      console.error(
        "Copy failed:",
        error
      );
    }
  };

  return (
    <div className="transaction-detail-row">

      <span className="transaction-detail-label">
        {label}
      </span>

      <div className="transaction-detail-value-wrapper">

        <span
  title={
    typeof value === "string"
      ? value
      : undefined
  }
  className={
    mono
      ? "transaction-detail-value transaction-mono"
      : "transaction-detail-value"
  }
>
  {value}
</span>

        {copy &&
          typeof value === "string" &&
          value !== "—" && (
            <button
              type="button"
              className="transaction-copy-button"
              onClick={handleCopy}
            >
              {copied
                ? "Copied"
                : "Copy"}
            </button>
          )}

      </div>
    </div>
  );
}

export default UserInfo;